import React, {useContext, useEffect, useRef, useState} from "react";
import {UserContext} from "@context/UserContext";
import {StudentSelect, ClassSelect} from "@common/StudentOrClassSelector";
import TableFilterControls from "@common/TableFilterControls";
import TableFilterControlsForm from "@common/TableFilterControlsForm";
import TableFilterControlsSection from "@common/TableFilterControlsSection";
import {Form, Radio} from "antd";
import TabGroupWrap from "../nav/TabGroupWrap";

const PracticeQuestionsResults = props => {
    const {difficultyLevels, results, questionId, previewMode, loadQuestionById, updateResultsFilter} = props;
    const {user} = useContext(UserContext);

    if (!results) {
        return;
    }

    if (previewMode) {
        return (
            <div className="ch-pq-results">
                <PreviewMarker
                    results={results}
                    questionId={questionId}
                    loadQuestionById={loadQuestionById}
                />
            </div>
        )
    } else if (user.student) {
        return (
            <div className="ch-pq-results">
                <StudentResultsMarker
                    difficultyLevels={difficultyLevels}
                    results={results}
                    questionId={questionId}
                />
            </div>
        )
    } else {
        return (
            <div className="ch-pq-results">
                <TeacherResultsMarker
                    difficultyLevels={difficultyLevels}
                    results={results}
                    questionId={questionId}
                    loadQuestionById={loadQuestionById}
                    updateResultsFilter={updateResultsFilter}
                />
            </div>
        )
    }
}

const StudentResultsMarker = props => {
    const {results} = props;

    const resultEntries = Object.values(results);
    const total = resultEntries.length;
    const correct = resultEntries.filter(r => r.result === true).length;
    const incorrect = resultEntries.filter(r => r.result === false).length;

    const sortedEntries = [...resultEntries].sort((a, b) => {
        const getPriority = (res) => {
            if (res.result === true) return 1;
            if (res.result === false) return 2;
            return 3;
        };
        return getPriority(a) - getPriority(b);
    });

    return (
        <TableFilterControls
            filterContent={
                <TableFilterControlsForm
                    style={{marginBottom: 'var(--spacing-unit)'}}
                    onSubmit={(e) => e.preventDefault()}
                >
                    <div
                        className="ch-resultMarkers"
                        role="img"
                        aria-label={`Progress: ${correct} correct, ${incorrect} incorrect, out of ${total} total questions.`}
                    >
                        {sortedEntries.map((resultResult) => {
                            const resultClass = resultResult.result != null ? ` ch-resultMarker-${resultResult.result ? 'c' : 'i'}` : "";
                            return (
                                <div
                                    key={resultResult.questionId}
                                    className={`ch-resultMarker${resultClass}`}
                                    aria-hidden="true"
                                ></div>
                            )
                        })}
                    </div>
                </TableFilterControlsForm>
            }
        />
    )
}

const filterValues = {
    NO_RESULTS: 'noResults',
    CLASS: 'class',
    STUDENT: 'student',
    MY_STUDENTS: 'myStudents',
};

const filterOptions = [
    {value: filterValues.NO_RESULTS, label: 'None'},
    {value: filterValues.CLASS, label: 'Class'},
    {value: filterValues.STUDENT, label: 'Student'},
    {value: filterValues.MY_STUDENTS, label: 'All'},
];

const TeacherResultsMarker = props => {
    const {results, questionId, loadQuestionById, updateResultsFilter} = props;
    const [filterType, setFilterType] = useState(filterValues.NO_RESULTS);
    const [filterId, setFilterId] = useState(null);

    const handleFilterChange = (type, value) => {
        setFilterType(type);
        setFilterId(value ?? null);
    }

    useEffect(() => {
        if (filterType === filterValues.NO_RESULTS) {
            updateResultsFilter(null);
        } else if (filterType === filterValues.CLASS) {
            updateResultsFilter({classId: filterId});
        } else if (filterType === filterValues.STUDENT) {
            updateResultsFilter({studentId: filterId});
        } else if (filterType === filterValues.MY_STUDENTS) {
            updateResultsFilter({allStudents: true});
        }
    }, [filterType, filterId]);

    return (
        <TableFilterControls
            filterContent={
                <TableFilterControlsForm
                    style={{marginBottom: "var(--spacing-unit)"}}
                    onSubmit={(e) => e.preventDefault()}
                >
                    <TableFilterControlsSection style={{flexWrap: 'wrap'}}>
                        <Form.Item
                            label="View results by"
                        >
                            <Radio.Group
                                buttonStyle={"solid"}
                                options={filterOptions}
                                optionType="button"
                                onChange={(e) => handleFilterChange(e.target.value)}
                                value={filterType}
                            />
                        </Form.Item>
                        {(filterType === filterValues.CLASS || filterType === filterValues.STUDENT) && (
                            <Form.Item style={{flexBasis: '100%'}}>
                                {filterType === filterValues.CLASS && (
                                    <ClassSelect onChange={classId => handleFilterChange(filterValues.CLASS, classId)} />
                                )}
                                {filterType === filterValues.STUDENT && (
                                    <StudentSelect onChange={studentId => handleFilterChange(filterValues.STUDENT, studentId)} />
                                )}
                            </Form.Item>
                        )}
                    </TableFilterControlsSection>
                    <QuestionsNavigation
                        results={results}
                        questionId={questionId}
                        loadQuestionById={loadQuestionById}
                    />
                </TableFilterControlsForm>
            }
        />
    )
}

const PreviewMarker = props => {
    const {results, questionId, loadQuestionById} = props;

    return (
        <TableFilterControls
            filterContent={
                <TableFilterControlsForm
                    style={{marginBottom: "var(--spacing-unit)"}}
                    onSubmit={(e) => e.preventDefault()}
                >
                    <Form.Item
                        label="Questions:"
                    >
                        <QuestionsNavigation
                            results={results}
                            questionId={questionId}
                            loadQuestionById={loadQuestionById}
                            showResults={false}
                        />
                    </Form.Item>
                </TableFilterControlsForm>
            }
        />
    )
}

const QuestionsNavigation = props => {
    const {results, questionId, loadQuestionById, showResults = true} = props;
    const tabGroupRef = useRef(null);
    const buttonGroupRef = useRef(null);

    useEffect(() => {
        if (!results) return;

        const handle = requestAnimationFrame(() => {
            tabGroupRef.current?.build();
        });

        return () => cancelAnimationFrame(handle);
    }, [results, questionId]);

    return (
        <TabGroupWrap
            ref={tabGroupRef}
            targetRef={buttonGroupRef}
            cellSelector=".ch-resultMarker-btn"
            cellActive="ch-resultMarker-active"
        >
            <div
                ref={buttonGroupRef}
                className="ch-resultMarkers"
                style={{marginTop: "var(--spacing-unit)"}}
                role="toolbar"
                aria-label="Question navigation"
            >
                {Object.entries(results).map(([key, resultResult]) => {
                    const isCurrent = questionId === resultResult.questionId;
                    const resultStatus = resultResult.result === true ? 'Correct' : resultResult.result === false ? 'Incorrect' : 'Not attempted';
                    const resultClass = resultResult.result != null ? `ch-resultMarker-${resultResult.result ? 'c' : 'i'}` : "";
                    const activeClass = questionId === resultResult.questionId ? "ch-resultMarker-active" : "";

                    return (
                        <button
                            key={resultResult.questionId}
                            className={[
                                "ch-resultMarker",
                                "ch-resultMarker-btn",
                                (showResults) && resultClass,
                                (activeClass) && activeClass,
                            ].filter(Boolean).join(" ")}
                            onClick={() => loadQuestionById?.(resultResult.questionId)}
                            aria-label={`Question ${resultResult.label}: ${resultStatus}`}
                            aria-current={isCurrent ? "step" : undefined}
                        >
                            {resultResult.label}
                        </button>
                    )
                })}
            </div>
        </TabGroupWrap>
    )
}

export default PracticeQuestionsResults;